import { Color3, type Scene, ShaderStore, StandardMaterial } from "@babylonjs/core";
import { CellMaterial } from "@babylonjs/materials";
import { cellPixelShader } from "@babylonjs/materials/cell/cell.fragment.js";
import { cellVertexShader } from "@babylonjs/materials/cell/cell.vertex.js";

// CellMaterial lazy-loads its shaders via dynamic import. Under Vite's dev prebundle that can
// register them too late (or into another copy of the store), and Babylon then falls back to
// fetching "cell.vertex.fx" by URL — which the SPA fallback answers with index.html. Register
// them eagerly in the store the engine actually uses.
for (const sh of [cellVertexShader, cellPixelShader]) ShaderStore.ShadersStore[sh.name] ??= sh.shader;

const cache = new Map<string, CellMaterial | StandardMaterial>();

/** Cel-shaded material (quantized lighting) — the base of the toon look. */
export function toon(scene: Scene, hex: string, opts: { high?: boolean } = {}): CellMaterial {
  const key = `${scene.getUniqueId()}:toon:${hex}:${opts.high ? 1 : 0}`;
  let m = cache.get(key) as CellMaterial | undefined;
  if (!m) {
    m = new CellMaterial(key, scene);
    m.onDisposeObservable.addOnce(() => cache.delete(key));
    m.diffuseColor = Color3.FromHexString(hex);
    m.computeHighLevel = opts.high ?? true;
    m.maxSimultaneousLights = 2;
    cache.set(key, m);
  }
  return m;
}

/** Unlit emissive material for VFX, visors, projectiles. */
export function glow(scene: Scene, hex: string, alpha = 1): StandardMaterial {
  const key = `${scene.getUniqueId()}:glow:${hex}:${alpha}`;
  let m = cache.get(key) as StandardMaterial | undefined;
  if (!m) {
    m = new StandardMaterial(key, scene);
    m.onDisposeObservable.addOnce(() => cache.delete(key));
    const c = Color3.FromHexString(hex);
    m.emissiveColor = c;
    m.diffuseColor = Color3.Black();
    m.specularColor = Color3.Black();
    m.disableLighting = true;
    m.alpha = alpha;
    if (alpha < 1) m.backFaceCulling = false;
    cache.set(key, m);
  }
  return m;
}

export function clearMaterialCache() {
  cache.clear();
}
